import { create } from "zustand";
import { api } from "@/lib/api";

export type Track = {
  id: string;
  title: string;
  duration?: number;
  is_instrumental?: boolean;
  album?: { id: string; title: string };
  album_title?: string;
};

type PlayerState = {
  queue: Track[];
  index: number;
  current: Track | null;
  url: string | null;
  isPlaying: boolean;
  loading: boolean;
  setPlaying: (p: boolean) => void;
  playTrack: (track: Track, queue?: Track[]) => Promise<void>;
  next: () => Promise<void>;
  prev: () => Promise<void>;
};

async function fetchStreamUrl(id: string): Promise<string> {
  if (id.startsWith("demo-")) {
    return `/music/${id}.mp3`;
  }
  const { data } = await api.get(`/api/tracks/${id}/stream`);
  return data.url || data.streamUrl || data.signedUrl || data;
}

export const usePlayer = create<PlayerState>((set, get) => ({
  queue: [],
  index: -1,
  current: null,
  url: null,
  isPlaying: false,
  loading: false,
  setPlaying: (p) => set({ isPlaying: p }),
  playTrack: async (track, queue) => {
    const q = queue && queue.length ? queue : [track];
    const idx = q.findIndex((t) => t.id === track.id);
    set({ loading: true, current: track, queue: q, index: idx >= 0 ? idx : 0 });
    try {
      api.post("/api/history", { track_id: track.id }).catch(() => {});
      const url = await fetchStreamUrl(track.id);
      set({ url, isPlaying: true, loading: false });
    } catch {
      set({ loading: false });
    }
  },
  next: async () => {
    const { queue, index } = get();
    if (index < queue.length - 1) {
      await get().playTrack(queue[index + 1], queue);
    }
  },
  prev: async () => {
    const { queue, index } = get();
    if (index > 0) {
      await get().playTrack(queue[index - 1], queue);
    }
  },
}));
